import { SurveyQuestionType } from '@posthog/browser-common';
import { useState } from 'react';
import type { MultipleSurveyQuestion } from 'posthog-js';

import FormGroup from '../../Forms/FormGroup';
import LabeledCheckbox from '../../Forms/LabeledCheckbox';
import LabeledCheckboxWithInput from '../../Forms/LabeledCheckboxWithInput';

import type { QuestionRendererProps } from './registry';

type ContentProps = {
  question: MultipleSurveyQuestion;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
};

const composeAnswer = (regulars: string[], openOn: boolean, text: string) => {
  if (openOn && text !== '') {
    return [...regulars, text];
  }
  return regulars;
};

const MultipleChoiceQuestionContent = ({ question, value, onChange }: ContentProps) => {
  const { choices, hasOpenChoice } = question;
  const regularChoices = hasOpenChoice ? choices.slice(0, -1) : choices;
  const openChoice = hasOpenChoice ? choices[choices.length - 1] : null;

  const initialArray = Array.isArray(value) ? value : [];
  const initialOpenText = openChoice
    ? initialArray.find((v) => !regularChoices.includes(v)) ?? ''
    : '';

  const [isOpenSelected, setIsOpenSelected] = useState<boolean>(initialOpenText !== '');
  const [stickyText, setStickyText] = useState<string>(initialOpenText);

  const regularSelected = initialArray.filter((v) => regularChoices.includes(v));

  const toggleRegular = (choice: string) => {
    const next = regularSelected.includes(choice)
      ? regularSelected.filter((c) => c !== choice)
      : [...regularSelected, choice];
    onChange(composeAnswer(next, isOpenSelected, stickyText));
  };

  const toggleOpen = () => {
    const isNextOpen = !isOpenSelected;
    setIsOpenSelected(isNextOpen);
    onChange(composeAnswer(regularSelected, isNextOpen, stickyText));
  };

  const handleTextChange = (text: string) => {
    setStickyText(text);
    onChange(composeAnswer(regularSelected, isOpenSelected, text));
  };

  return (
    <FormGroup legend={question.question} spacing="sm">
      {regularChoices.map((choice) => (
        <LabeledCheckbox
          key={choice}
          label={choice}
          isChecked={regularSelected.includes(choice)}
          onChange={() => toggleRegular(choice)}
        />
      ))}
      {openChoice && (
        <LabeledCheckboxWithInput
          key={openChoice}
          label={openChoice}
          isChecked={isOpenSelected}
          onChange={toggleOpen}
          inputValue={stickyText}
          onInputChange={handleTextChange}
        />
      )}
    </FormGroup>
  );
};

/**
 * Renders a PostHog multiple-choice question as a column of checkboxes. When
 * the question has `hasOpenChoice: true`, the last choice becomes a
 * `LabeledCheckboxWithInput` — its typed value joins the answer array in
 * place of the choice's literal label. The typed value is "sticky": it
 * survives uncheck/recheck within the same step lifetime, but only contributes
 * to the captured answer array when both checked AND non-empty.
 */
export const MultipleChoiceQuestion = ({ question, value, onChange }: QuestionRendererProps) => {
  if (question.type !== SurveyQuestionType.MultipleChoice) {
    return null;
  }
  return <MultipleChoiceQuestionContent question={question} value={value} onChange={onChange} />;
};
